import { Component } from '@theme/component';

/**
 * The site-wide quote: summary, request form, submission.
 *
 * Ported from components/offerte/OfferteModal.js, OfferteSamenvatting.js and
 * OfferteFormulier.js, with two things the prototype could not have:
 *
 *  · The cart is the quote. The prototype keeps its lines in React state and
 *    mirrors them into localStorage; on Shopify the cart is already global and
 *    already persisted, so /cart.js is the only source of truth and nothing is
 *    written to browser storage.
 *
 *  · A real backend. The prototype's form calls `onVerzonden()` and stops.
 *    This posts to `/apps/quote/quotations/submit`, the app proxy the live
 *    theme uses, with the payload shape that proxy already accepts — see
 *    snippets/quote-modal.liquid in the live theme.
 *
 * @extends {Component}
 */
class AwhQuote extends Component {
  #files = [];
  #groups = [];
  #timer = null;
  #optionMaps = new Map();
  #lastFocus = null;
  #sent = false;

  /** /cart renders the same component as a page rather than a panel. */
  get #inline() {
    return this.hasAttribute('data-inline');
  }

  connectedCallback() {
    super.connectedCallback();

    this.#bindDrop();

    // Inline there is nothing to open: the quote is the page, so it loads now
    // and stays loaded. Everything below it — the summary, the form, the
    // submission, the address lookup — is the same code the panel runs.
    if (this.#inline) {
      document.body.style.overflow = '';
      this.load();
    }
    this.refs.form?.addEventListener('submit', this.#submit);
    document.addEventListener('keydown', this.#onKey);
    document.addEventListener('awh:quote:open', this.openWithToast);
    document.addEventListener('awh:quote:refresh', this.refresh);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#onKey);
    document.removeEventListener('awh:quote:open', this.openWithToast);
    document.removeEventListener('awh:quote:refresh', this.refresh);
    clearTimeout(this.#timer);
  }

  /* ------------------------------------------------------------ open/close */

  get #isOpen() {
    if (this.#inline) return true;
    return this.refs.dialog?.classList.contains('is-open') ?? false;
  }

  open = () => {
    if (this.#inline || this.#isOpen) return;
    this.#lastFocus = document.activeElement;
    this.querySelector('.qmodal__scrim')?.classList.add('is-open');
    this.refs.dialog?.classList.add('is-open');
    this.refs.dialog?.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    // The panel, not the close button. Focus has to enter the dialog for
    // keyboard and screen-reader users, but putting it on a control drew a
    // focus ring on the × every single time the panel opened. From here the
    // first Tab reaches the close button and rings it properly.
    this.refs.paneel?.focus();
    this.#skeleton();
    this.load();
  };

  /** Opened by an add — announce it for anyone who cannot see the panel move. */
  openWithToast = () => {
    this.open();
    this.#toast('Toegevoegd aan uw offerte');
  };

  /** `event.target`, not `currentTarget` — component.js delegates on document. */
  close = () => {
    if (this.#inline) return;
    this.querySelector('.qmodal__scrim')?.classList.remove('is-open');
    this.refs.dialog?.classList.remove('is-open');
    this.refs.dialog?.setAttribute('inert', '');
    document.body.style.overflow = '';
    /** @type {HTMLElement|null} */ (this.#lastFocus)?.focus?.();

    // Give the panel time to animate out before the success state disappears.
    if (this.#sent) {
      setTimeout(() => {
        this.#sent = false;
        this.#show('body');
      }, 300);
    }
  };

  #onKey = (event) => {
    // The page has no dialog to trap focus in or to close with Escape.
    if (this.#inline || !this.#isOpen) return;

    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = [
      ...(this.refs.paneel?.querySelectorAll(
        'a[href], button:not([disabled]), input:not([tabindex="-1"]), select, textarea'
      ) ?? []),
    ].filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /** The suggestion row's heading is its disclosure. */
  toggleFamily = () => {
    const btn = this.refs.familieKnop;
    const list = this.refs.familieLijst;
    if (!btn || !list) return;
    const open = list.hidden;
    list.hidden = !open;
    btn.setAttribute('aria-expanded', String(open));
  };

  toggleSummary = () => {
    const on = this.refs.samkolom?.classList.toggle('is-open');
    this.refs.sambalk?.setAttribute('aria-expanded', String(Boolean(on)));
  };

  #toast(text) {
    const el = this.refs.toast;
    if (!el) return;
    el.textContent = text;
    el.classList.add('is-open');
    setTimeout(() => el.classList.remove('is-open'), 3200);
  }

  /**
   * Something the right size, immediately.
   *
   * The panel used to open as a bare header and jump to full height a moment
   * later, when /cart.js came back — a 500px lurch on every open. Three grey
   * rows in the shape of quote lines hold the space until the real ones
   * arrive, and the form beside them is server-rendered so it never moved.
   */
  #skeleton() {
    if (this.#groups.length || this.#sent) return;
    this.#show('body');
    if (!this.refs.items) return;
    this.refs.items.innerHTML = Array.from(
      { length: 2 },
      () => `
      <li class="qsam__item qsam__item--skelet" aria-hidden="true">
        <span class="qsam__foto qsam__skelet"></span>
        <div class="qsam__body">
          <span class="qsam__skelet qsam__skelet--titel"></span>
          <span class="qsam__skelet qsam__skelet--regel"></span>
          <span class="qsam__skelet qsam__skelet--prijs"></span>
        </div>
      </li>`
    ).join('');
  }

  /** Which of the three panel states is showing. */
  #show(state) {
    const map = { body: this.refs.body, leeg: this.refs.leeg, klaar: this.refs.klaar };
    for (const [name, el] of Object.entries(map)) if (el) el.hidden = name !== state;
    if (this.refs.titel && !this.#inline) {
      this.refs.titel.textContent = state === 'klaar' ? 'Aanvraag verzonden' : 'Offerte aanvragen';
    }

    // On /cart the three states share one <h1>, so it has to say what is on
    // screen. Three headings, two of them hidden, is three H1s to a crawler.
    if (this.refs.paginakop) {
      const titles = { klaar: 'Aanvraag verzonden', leeg: 'Uw offerte is leeg', body: 'Uw gegevens' };
      this.refs.paginakop.textContent = titles[state] ?? titles.body;
    }
  }

  /* ------------------------------------------------------------------ cart */

  refresh = () => {
    if (this.#isOpen) this.load();
    else this.#count();
  };

  /**
   * Two passes, because one of them is slow.
   *
   * Resolving an option's price means fetching the product page for its option
   * set, which is a full HTML document. Waiting for that before drawing
   * anything left the panel an empty white box for a second or two after every
   * open. So the cart is drawn immediately from what the line already carries,
   * and the option set corrects it a moment later — the figures agree in all
   * but the case the second pass exists for, a price changed in the app since
   * the line was added.
   */
  async load() {
    if (this.#sent) return;
    try {
      const cart = await fetch(`${root()}cart.js`).then((r) => r.json());

      this.#groups = await this.#group(cart, false);
      this.#render(cart);

      this.#groups = await this.#group(cart, true);
      this.#render(cart);
    } catch {
      // A failed cart read must not leave a half-drawn panel behind.
      this.#show('leeg');
    }
  }

  /**
   * Group the cart into one line per configured product.
   *
   * Optionize writes add-ons two ways and both are live: the current format
   * bundles them into a `_oz-addons` JSON property on the main line, the older
   * one adds a separate line per add-on linked by `_oz-key`. Same grouping as
   * the live theme's quote modal, for the same reason — the summary has to
   * show a poort with its options under it, not eight unexplained lines.
   */
  async #group(cart, resolve) {
    const groups = {};

    for (const item of cart.items ?? []) {
      const raw = item.properties?.['_oz-addons'];

      if (raw) {
        let parsed;
        try {
          parsed = JSON.parse(raw);
        } catch {
          parsed = { a: [] };
        }
        const key = `bundled-${item.key || item.variant_id}`;
        groups[key] ??= { main: null, addons: [], bundled: true };
        groups[key].main = item;

        if (parsed.a?.length) {
          const names = resolve ? await this.#optionMap(item.handle, item.product_id) : null;
          groups[key].addons = parsed.a.map((addon) => {
            const found = names?.byVariant[String(addon.id)] ?? { name: addon.n, value: addon.n };
            return { label: found.value, price: Number.parseInt(addon.p, 10) || 0 };
          });
        }
      } else {
        const ozKey = item.properties?.['_oz-key'] ?? '';
        const isAddon = item.properties?.['Type'] === 'Add-on';
        // `item.key`, not the variant id: two different configurations of the
        // same poort are two cart lines with the same variant, and keying on
        // the variant silently collapsed them into one.
        const key = isAddon
          ? ozKey.replace(/^addon-(\d+).*/, 'main-$1')
          : ozKey || `line-${item.key}`;

        groups[key] ??= { main: null, addons: [], bundled: false };
        if (isAddon) {
          groups[key].addons.push({
            label: item.properties?.['Option value'] ?? item.title,
            price: item.final_price ?? item.price,
            key: item.key,
          });
        } else {
          groups[key].main = item;
        }
      }
    }

    const list = Object.values(groups).filter((g) => g.main);

    // Choices that cost nothing never became line items — they are line-item
    // properties on the parent. Without this the summary showed a bare variant
    // where the prototype lists "120x120 palen · Nee, met slot en cilinder",
    // which is most of what the customer actually chose.
    // The Optionize extension patches window.fetch and appends its own record
    // of every option to each add, declines included — so a line arrives with
    // "Nee · Nee · Nee, zelf plaatsen" and the same value twice where the store
    // has two options sharing a label. OfferteSamenvatting.js drops declines
    // for exactly this reason: they tell the customer nothing and bury the
    // choices that matter. Applied here, so it holds whoever wrote the
    // property.
    const skip = new Set(['Maat', 'Kleur', 'Type', 'Option name', 'Option value']);
    const declined = (v) => /^\s*nee\b/i.test(v);

    for (const g of list) {
      // Priced choices, resolved against the product's option set rather than
      // against the copy taken when the line was added — the option set is what
      // the app charges from, so a price change there must not leave old lines
      // quoting the old figure.
      g.priced = resolve
        ? await this.#resolve(g.main)
        : ours(g.main).map((r) => ({ name: r.name, label: r.label, price: r.price, id: null }));

      const overridden = new Set(g.priced.map((a) => a.name));
      const seen = new Set();
      const included = Object.entries(g.main.properties ?? {})
        // Optionize's patched fetch stamps its own default for every option on
        // the line, and its hidden UI never saw the visitor's choice: a line
        // that carries "Ja, met poortmotor" in our record also carries
        // "Draaipoort elektrisch?: Nee, met slot en cilinder" from the app.
        // Ours wins for any option we priced.
        .filter(([name, value]) => value && !name.startsWith('_') && !skip.has(name) && !overridden.has(name))
        .map(([, value]) => String(value))
        .filter((value) => !declined(value) && !seen.has(value) && seen.add(value))
        .map((value) => ({ label: value, price: 0 }));

      g.addons = [...included, ...g.priced, ...g.addons];
    }

    return list;
  }

  /**
   * The priced choices on a line, as the option set describes them today.
   *
   * The selection is ours — Optionize's own property on the line is its default,
   * not what the visitor picked. The price and the add-on variant come from the
   * option set, so what goes to the proxy is the app's own figure, sent as is.
   * The price recorded at add time is only a fallback for when the option set
   * cannot be read.
   */
  async #resolve(item) {
    const recorded = ours(item);
    if (!recorded.length) return [];

    const map = await this.#optionMap(item.handle, item.product_id);
    return recorded.map((r) => {
      const found = map.byChoice[`${r.name}\u0000${r.label}`];
      return {
        name: r.name,
        key: r.key,
        label: r.label,
        price: found && found.price > 0 ? found.price : r.price,
        id: found?.id ?? null,
      };
    });
  }

  /**
   * variantId -> { name, value } from the product's Optionize option set.
   *
   * The bundled format stores only an add-on variant id and a short label, so
   * the readable option name has to come from the option set on the product
   * page. Cached per product; a miss falls back to the short label rather than
   * showing nothing.
   */
  async #optionMap(handle, productId) {
    const key = String(productId);
    if (this.#optionMaps.has(key)) return this.#optionMaps.get(key);

    // Two indexes off the same pass: by add-on variant id, for lines Optionize
    // itself wrote, and by option name + value label, for ours. The second is
    // what makes the option set — not a copy taken at add time — the authority
    // on what an option costs and which variant represents it.
    let map = { byVariant: {}, byChoice: {} };
    try {
      const html = await fetch(`${root()}products/${handle}`).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const script = doc.getElementById(`optionize-option-set-${productId}`);
      if (script) {
        const set = JSON.parse(script.textContent ?? '{}');
        for (const option of set.options ?? []) {
          const name = option.label || option.name;
          for (const value of option.values ?? []) {
            const generated = value.generatedAddOnProductVariant;
            // "optionize-xxx-PRICE::VARIANT_ID"
            const id = generated ? generated.split('::').pop() : null;
            const price = Math.round((Number.parseFloat(String(value.addOnPrice ?? '0').replace(',', '.')) || 0) * 100);

            const entry = { name, value: value.label, id, price };
            if (id) map.byVariant[id] = entry;
            map.byChoice[`${name}\u0000${value.label}`] = entry;
          }
        }
      }
    } catch {
      map = { byVariant: {}, byChoice: {} };
    }

    this.#optionMaps.set(key, map);
    return map;
  }

  #render(cart) {
    const count = this.#groups.length;
    this.#count(cart.item_count);

    if (!count) {
      this.#show('leeg');
      return;
    }
    this.#show('body');

    let subtotal = 0;
    const html = this.#groups
      .map((g, i) => {
        const main = g.main;
        const line = (main.final_price ?? main.price) * main.quantity;
        subtotal += line;

        // Every choice goes behind one disclosure, priced or not. Listing the
        // included ones in the open pushed the price four lines down the card
        // and buried the thing the customer is actually scanning for.
        const chosen = g.addons;
        const paid = chosen.filter((a) => a.price > 0);
        if (!g.bundled) for (const a of paid) subtotal += a.price;
        const addonSum = paid.reduce((s, a) => s + a.price, 0);

        // One meaningful line instead of four of small mono text. The variant
        // title is the store's raw option values — "4000x1200 / Zwart (verzinkt
        // + RAL 9005)" — which wrapped to two lines and read as machine output.
        const meta = [tidySize(main.properties?.Maat), tidyColour(main.properties?.Kleur)]
          .filter(Boolean)
          .join(' · ');
        const variant =
          meta || (main.variant_title && main.variant_title !== 'Default Title' ? main.variant_title : '');
        // `?width=` — the `_160x` filename suffix only resolves for some assets
        // and silently 404s for the rest, which is why the thumbnail was blank.
        const photo = main.image ? `${main.image}${main.image.includes('?') ? '&' : '?'}width=160` : '';

        return `
      <li class="qsam__item">
        ${photo ? `<img class="qsam__foto" src="${esc(photo)}" alt="" loading="lazy">` : ''}
        <div class="qsam__body">
          <div class="qsam__kop">
            <p class="qsam__naam">${esc(main.product_title ?? main.title)}</p>
            <button type="button" class="qsam__weg" data-remove="${i}"
                    aria-label="${esc(main.product_title ?? main.title)} uit de offerte verwijderen"
                    on:click="/remove">
              ${CLOSE}
            </button>
          </div>
          ${variant ? `<p class="qsam__variant">${esc(variant)}</p>` : ''}
          ${
            chosen.length
              ? `<div class="qsam__addons">
                   <button type="button" class="qsam__addons-knop" aria-expanded="false" on:click="/toggleAddons">
                     ${CHEVRON}
                     ${chosen.length} ${chosen.length === 1 ? 'keuze' : 'keuzes'}
                     ${addonSum > 0 ? `<span class="qsam__addons-som">+ ${euro(addonSum / 100)}</span>` : ''}
                   </button>
                   <ul class="qsam__addonlijst" role="list" hidden>
                     ${chosen
                       .map(
                         (a) =>
                           `<li><span class="qsam__addon-waarde">${esc(a.label)}</span>${
                             a.price > 0
                               ? `<span class="qsam__addon-prijs">+ ${euro(a.price / 100)}</span>`
                               : ''
                           }</li>`
                       )
                       .join('')}
                   </ul>
                 </div>`
              : ''
          }
          <p class="qsam__prijs">${euro((line + (g.bundled ? 0 : addonSum)) / 100)}</p>
        </div>
      </li>`;
      })
      .join('');

    if (this.refs.items) {
      this.refs.items.innerHTML = html;
      // CSS cannot ask whether a box overflows, and the fade must not appear
      // when it does not — it would dim the last line of a short quote.
      // Measured synchronously: on a rAF the second render pass had already
      // replaced the list and the class landed on a detached box.
      const box = this.refs.items;
      box.classList.toggle('is-scrollable', box.scrollHeight > box.clientHeight);
    }
    if (this.refs.subtotaal) this.refs.subtotaal.textContent = euro(subtotal / 100);
    if (this.refs.totaal) this.refs.totaal.textContent = euro(subtotal / 100);
    if (this.refs.balkAantal) this.refs.balkAantal.textContent = String(count);
    if (this.refs.balkWoord) this.refs.balkWoord.textContent = count === 1 ? 'product' : 'producten';
    if (this.refs.balkTotaal) this.refs.balkTotaal.textContent = euro(subtotal / 100);

    this.#family();
  }

  /**
   * "Maak de set compleet" — the rest of the model family.
   *
   * Never another product from the same category: "andere schuifpoorten" is a
   * different block with a different job, and mixing the two turns a
   * complete-the-set suggestion into a generic carousel. Anything already in
   * the quote is dropped — suggesting what somebody just added is the fastest
   * way to make a block like this read as automated.
   */
  #family() {
    const row = this.refs.bijpassend;
    const list = this.refs.familieLijst;
    if (!row || !list) return;

    const inQuote = new Set(this.#groups.map((g) => g.main.handle));
    const catalogue = familyCatalogue();
    const seen = new Set();
    const picks = [];

    for (const g of this.#groups) {
      const key = modelKey(g.main.product_title ?? g.main.title);
      if (!key) continue;
      const mine = catalogue.find((x) => x.h === g.main.handle);

      const perCategory = new Map();
      for (const x of catalogue) {
        if (inQuote.has(x.h) || seen.has(x.h)) continue;
        if (mine && x.c === mine.c) continue;
        if (modelKey(x.t) !== key) continue;
        // One per category: the family has at most one member in each, and if
        // the data ever grows a second the row should not double up on it.
        if (!perCategory.has(x.c)) perCategory.set(x.c, x);
      }
      for (const x of perCategory.values()) {
        seen.add(x.h);
        picks.push(x);
      }
    }

    if (!picks.length) {
      row.hidden = true;
      return;
    }

    picks.sort((a, b) => ORDER.indexOf(a.c) - ORDER.indexOf(b.c));
    const shown = picks.slice(0, 3);

    // One code names the set when every suggestion belongs to the same family,
    // which is the common case. With two gates in the quote it would name only
    // one of them, so it says nothing rather than something misleading.
    const codes = new Set(shown.map((x) => modelCode(x.t)).filter(Boolean));
    if (this.refs.familieCode) {
      this.refs.familieCode.textContent = codes.size === 1 ? [...codes][0] : '';
    }

    row.hidden = false;
    list.innerHTML = shown
      .map(
        (x) => `
      <li>
        <a class="qbijp__kaart" href="${root()}products/${esc(x.h)}">
          <span class="qbijp__media">${
            x.i ? `<img src="${esc(x.i)}" alt="" loading="lazy" width="80" height="60">` : ''
          }</span>
          <span class="qbijp__body">
            <span class="qbijp__naam">${esc(SINGULAR[x.c] ?? x.t)}</span>
            <span class="qbijp__prijs">vanaf ${euroWhole(x.p / 100)}</span>
          </span>
          <span class="qbijp__plus" aria-hidden="true">${PLUS}</span>
        </a>
      </li>`
      )
      .join('');
  }

  /** The header badge. Reads the cart when it is not told a figure. */
  async #count(known) {
    let n = known;
    if (n == null) {
      try {
        n = (await fetch(`${root()}cart.js`).then((r) => r.json())).item_count;
      } catch {
        return;
      }
    }
    // The header badge is server-rendered on load and patched here after every
    // add or removal, so it never disagrees with the panel behind it.
    for (const badge of document.querySelectorAll('[data-quote-count]')) {
      badge.textContent = String(n);
      badge.hidden = !n;
    }
    document.dispatchEvent(new CustomEvent('awh:quote:count', { detail: { count: n } }));
  }

  toggleAddons = (event) => {
    const btn = event.target?.closest?.('.qsam__addons-knop');
    const list = btn?.parentElement?.querySelector('.qsam__addonlijst');
    if (!btn || !list) return;
    const open = list.hidden;
    list.hidden = !open;
    btn.setAttribute('aria-expanded', String(open));
    btn.querySelector('.qsam__chev')?.classList.toggle('is-open', open);
  };

  remove = async (event) => {
    const btn = event.target?.closest?.('[data-remove]');
    if (!btn) return;
    const group = this.#groups[Number(btn.getAttribute('data-remove'))];
    if (!group) return;

    btn.setAttribute('disabled', '');
    const updates = { [group.main.key]: 0 };
    // Old-format add-ons are their own lines and have to go with the parent.
    for (const a of group.addons) if (a.key) updates[a.key] = 0;

    try {
      await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      await this.load();
    } catch {
      btn.removeAttribute('disabled');
    }
  };

  /* --------------------------------------------------------------- address */

  /**
   * Postcode + huisnummer -> straat + plaats, via PDOK.
   *
   * The same service the live theme calls. On a miss the two fields are
   * revealed for manual entry rather than the request being blocked.
   */
  lookupAddress = () => {
    const form = this.refs.form;
    if (!form) return;

    const postcode = (form.elements.postcode?.value ?? '').replace(/\s/g, '').toUpperCase();
    const number = (form.elements.huisnummer?.value ?? '').trim();
    if (!/^[0-9]{4}[A-Z]{2}$/.test(postcode) || !number) return;

    clearTimeout(this.#timer);
    this.#timer = setTimeout(async () => {
      this.#status('Adres opzoeken…');
      try {
        const url =
          'https://api.pdok.nl/bzk/locatieserver/search/v3_1/free?q=' +
          encodeURIComponent(`${postcode} ${number}`) +
          '&fq=type:adres&rows=1';
        const data = await fetch(url).then((r) => r.json());
        const doc = data?.response?.docs?.[0];

        if (doc?.straatnaam && doc?.woonplaatsnaam) {
          form.elements.straat.value = doc.straatnaam;
          form.elements.plaats.value = doc.woonplaatsnaam;
          this.#status(`${doc.straatnaam} ${number}, ${doc.woonplaatsnaam}`);
        } else {
          this.#status('Adres niet gevonden. Vul het hieronder handmatig in.', true);
        }
      } catch {
        this.#status('Adres ophalen mislukt. Vul het hieronder handmatig in.', true);
      }
    }, 400);
  };

  #status(text, manual = false) {
    const el = this.refs.adresstatus;
    if (el) {
      el.hidden = false;
      el.textContent = text;
    }
    if (manual && this.refs.adresvelden) this.refs.adresvelden.hidden = false;
  }

  /* ----------------------------------------------------------------- files */

  browse = () => this.refs.bestanden?.click();

  pickFiles = (event) => {
    this.#addFiles([...(event.target?.files ?? [])]);
    if (event.target) event.target.value = '';
  };

  #bindDrop() {
    const drop = this.refs.drop;
    if (!drop) return;
    drop.addEventListener('dragover', (e) => {
      e.preventDefault();
      drop.classList.add('is-over');
    });
    drop.addEventListener('dragleave', () => drop.classList.remove('is-over'));
    drop.addEventListener('drop', (e) => {
      e.preventDefault();
      drop.classList.remove('is-over');
      this.#addFiles([...(e.dataTransfer?.files ?? [])]);
    });
  }

  #addFiles(list) {
    const MAX = 5;
    const MB = 10;
    let error = null;

    for (const f of list) {
      if (this.#files.length >= MAX) {
        error = `Maximaal ${MAX} bestanden.`;
        break;
      }
      if (f.size > MB * 1024 * 1024) {
        error = `${f.name} is groter dan ${MB} MB.`;
        continue;
      }
      if (this.#files.some((x) => x.name === f.name && x.size === f.size)) continue;
      this.#files.push(f);
    }

    const fout = this.refs.bestandsfout;
    if (fout) {
      fout.hidden = !error;
      fout.textContent = error ?? '';
    }
    this.#renderFiles();
  }

  #renderFiles() {
    const list = this.refs.bestandenlijst;
    if (!list) return;
    list.hidden = this.#files.length === 0;
    list.innerHTML = this.#files
      .map(
        (f, i) => `
      <li>
        <span class="qform__bestand-naam">${esc(f.name)}</span>
        <span class="qform__bestand-grootte">${(f.size / 1024 / 1024).toFixed(1)} MB</span>
        <button type="button" class="qform__bestand-weg" data-file="${i}"
                aria-label="${esc(f.name)} verwijderen" on:click="/dropFile">${CLOSE}</button>
      </li>`
      )
      .join('');
  }

  dropFile = (event) => {
    const btn = event.target?.closest?.('[data-file]');
    if (!btn) return;
    this.#files.splice(Number(btn.getAttribute('data-file')), 1);
    this.#renderFiles();
  };

  /* ---------------------------------------------------------------- submit */

  #submit = async (event) => {
    event.preventDefault();
    const form = this.refs.form;
    const button = this.refs.verzend;
    if (!form) return;

    // Honeypot: a real visitor never sees this field, so anything in it is a
    // bot. Accept silently and discard rather than showing an error.
    if (form.elements.website?.value) {
      this.#sent = true;
      this.#show('klaar');
      return;
    }

    if (!form.reportValidity()) return;

    const street = form.elements.straat?.value?.trim() ?? '';
    const city = form.elements.plaats?.value?.trim() ?? '';
    if (!street || !city) {
      if (this.refs.adresvelden) this.refs.adresvelden.hidden = false;
      this.#error('Vul uw straat en plaats in — wij konden ze niet automatisch ophalen.');
      return;
    }

    const number = form.elements.huisnummer.value.trim();
    const postcode = form.elements.postcode.value.trim();

    // The proxy takes no timing field, so it is prepended to the notes rather
    // than dropped. See snippets/awh-quote-form.liquid.
    const timing = form.elements.timing;
    const when = timing?.options?.[timing.selectedIndex]?.text;
    const notes = [when ? `Gewenste plaatsing: ${when}` : '', form.elements.opmerkingen.value.trim()]
      .filter(Boolean)
      .join('\n\n');

    const payload = {
      firstName: form.elements.naam.value.trim(),
      email: form.elements.email.value.trim(),
      phone: form.elements.telefoon.value.trim(),
      address: `${street} ${number}, ${postcode} ${city}`,
      customerNotes: notes,
      website: '',
      cart: await this.#expanded(),
    };

    button?.setAttribute('disabled', '');
    button?.classList.add('is-busy');
    this.#error(null);

    try {
      let response;
      if (this.#files.length) {
        const body = new FormData();
        body.append('data', JSON.stringify(payload));
        for (const f of this.#files) body.append('files', f);
        // No Content-Type header — the browser sets it with the boundary.
        response = await fetch(`${root()}apps/quote/quotations/submit`, { method: 'POST', body });
      } else {
        response = await fetch(`${root()}apps/quote/quotations/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      const result = await response.json();
      if (!result.success) throw new Error(result.errors ? result.errors.join(', ') : result.error);

      this.#sent = true;
      form.reset();
      this.#files = [];
      this.#renderFiles();
      if (this.refs.adresvelden) this.refs.adresvelden.hidden = true;
      if (this.refs.adresstatus) this.refs.adresstatus.hidden = true;

      if (this.refs.nummer) {
        this.refs.nummer.hidden = !result.quotationNumber;
        this.refs.nummer.textContent = result.quotationNumber
          ? `Uw referentie: ${result.quotationNumber}`
          : '';
      }
      this.#show('klaar');

      await fetch(`${root()}cart/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      this.#groups = [];
      this.#count(0);
    } catch (error) {
      this.#error('Er is een fout opgetreden. Probeer het opnieuw.');
      console.error('[awh-quote] submission failed', error);
    } finally {
      button?.removeAttribute('disabled');
      button?.classList.remove('is-busy');
    }
  };

  #error(text) {
    const el = this.refs.fout;
    if (!el) return;
    el.hidden = !text;
    el.textContent = text ?? '';
  }

  /**
   * The cart, with bundled add-ons expanded back into separate line items.
   *
   * The proxy predates Optionize's bundled format and reads add-ons as their
   * own lines. Expanding here rather than changing the app keeps one contract
   * for both themes — the live theme does exactly the same thing.
   */
  async #expanded() {
    const cart = await fetch(`${root()}cart.js`).then((r) => r.json());
    const items = [];

    /*
     * The counter starts at a timestamp rather than at 1, which the old theme
     * did too and for a reason worth writing down: the quote app pairs an
     * add-on with its parent by substring —
     *
     *     addonKey.includes(itemKey.replace('main-', ''))
     *
     * With `main-1` that test is `'addon-1'.includes('1')`, which is also true
     * of `addon-10`, `addon-11` and `addon-12`. A quote holding ten or more
     * products would hand item 1 the options of items 10 upward. Long keys
     * cannot collide, so the app's loose comparison stops mattering.
     */
    let n = Date.now();

    for (const item of cart.items ?? []) {
      const mine = await this.#resolve(item);
      const vrij = freeOurs(item);
      const keys = ozKeys(item);
      if (mine.length || vrij.length || keys.length) {
        // Expand our own priced options into the add-on lines the proxy reads,
        // and take them back out of nothing — the Shopify line never carried
        // them, so the parent price is already the bare variant.
        n += 1;
        const properties = { ...item.properties };
        delete properties['_awh_opties'];
        delete properties['_awh_vrij'];
        delete properties['_awh_keys'];

        // Clear every property Optionize owns before writing ours back. It
        // stamps a default under each option's internal name, for questions the
        // visitor answered and for questions conditional logic never showed
        // them. Clearing first is what makes a hidden question leave no trace:
        // nothing answered it, so nothing writes it back.
        //
        // A cart from before this property existed has no list, so nothing is
        // cleared and the line behaves exactly as it did.
        for (const k of keys) delete properties[k];

        /*
         * Optionize stamps its own default on every option it owns, because the
         * UI that would have recorded the visitor's answer is the one this theme
         * hides. A line that really carries "Ja, met poortmotor" therefore also
         * carries "Schuifpoort elektrisch?: Nee, met slot en cilinder".
         *
         * The drawer already ignores those defaults for anything we priced. The
         * quotation did not: it listed the properties as given, so every paid
         * choice read "Nee", and the app pairs an add-on with a property by
         * matching name *and* value — so the add-on found nothing to attach to
         * and its price appeared nowhere either. Eight paid choices and €12.007
         * went missing from a €15.595 quotation.
         *
         * Writing our answer over the default fixes both halves at once: the
         * configuration reads what was actually chosen, and the add-on lines up
         * with it so its price prints beside it.
         */
        // Under the question as the visitor read it, not Optionize's internal
        // name: "Montage incl. gemetselde pilaren", not "Montage prefab
        // elektrisch". The name was only ever used here to land on top of the
        // default, and the default is already gone.
        for (const a of vrij) properties[a.name] = a.label;
        for (const a of mine) properties[a.name] = a.label;

        properties['_oz-key'] = `main-${n}`;
        items.push({ ...item, properties });

        for (const a of mine) {
          // The add-on's own generated variant, so the proxy resolves the same
          // product Optionize would have sent. Falls back to the parent variant
          // only when the option set gave us no generated variant to name.
          const id = a.id ? Number.parseInt(a.id, 10) : item.variant_id;
          items.push({
            id,
            product_id: item.product_id,
            variant_id: id,
            title: a.name,
            price: a.price,
            final_price: a.price,
            original_price: a.price,
            quantity: 1,
            vendor: item.vendor ?? '',
            featured_image: null,
            image: null,
            handle: item.handle,
            product_title: a.name,
            variant_title: a.label,
            properties: {
              Type: 'Add-on',
              // The app pairs an add-on to its property on name plus value, so
              // this has to be the same string the property above is written
              // under — the label.
              'Option name': a.name,
              'Option value': a.label,
              '_oz-key': `addon-${n}`,
            },
          });
        }
        continue;
      }

      const raw = item.properties?.['_oz-addons'];
      if (!raw) {
        items.push(item);
        continue;
      }

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = { a: [] };
      }
      n += 1;
      const names = await this.#optionMap(item.handle, item.product_id);

      const properties = { ...item.properties };
      delete properties['_oz-addons'];
      properties['_oz-key'] = `main-${n}`;

      // final_price already includes the add-ons, so take them back out of the
      // parent or the total is counted twice.
      const addonTotal = (parsed.a ?? []).reduce((s, a) => s + (Number.parseInt(a.p, 10) || 0), 0);
      const base = (item.final_price ?? item.price) - addonTotal;

      items.push({ ...item, properties, price: base, final_price: base, original_price: base });

      for (const addon of parsed.a ?? []) {
        const found = names.byVariant[String(addon.id)] ?? { name: addon.n, value: addon.n };
        const price = Number.parseInt(addon.p, 10) || 0;
        items.push({
          id: Number.parseInt(addon.id, 10),
          product_id: item.product_id,
          variant_id: Number.parseInt(addon.id, 10),
          title: found.name,
          price,
          final_price: price,
          original_price: price,
          quantity: addon.q || 1,
          vendor: item.vendor ?? '',
          featured_image: null,
          image: null,
          handle: item.handle,
          product_title: found.name,
          variant_title: found.value,
          properties: {
            Type: 'Add-on',
            'Option name': found.name,
            'Option value': found.value,
            '_oz-key': `addon-${n}`,
          },
        });
      }
    }

    /*
     * The totals have to be rebuilt, not passed through.
     *
     * The proxy does not add the lines up. proxy.quotations.submit.jsx stores
     * every line it is given, but takes the quotation total from the cart-level
     * `total_price` it was handed:
     *
     *     subtotal: items_subtotal_price / 100,
     *     total:    total_price / 100,
     *
     * For an Optionize cart that was safe: `final_price` already carried the
     * add-ons, the branch above takes them back out of the parent, and parent
     * plus add-ons comes back to the same number the cart already reported.
     *
     * Our own lines are different. awh-configure.js adds them at the bare
     * variant price — Optionize refuses its generated add-on variants as
     * sibling lines (422, "can only be purchased as an add-on with a main
     * product"), so the priced choices ride along in `_awh_opties` instead and
     * never reach the Shopify cart's totals. Passing `total_price` straight
     * through therefore quoted the gate without any of its options: nine
     * quotations between 7 and 16 September went out a total of €59.676 short.
     *
     * Summing the expanded lines is right for both shapes, because both end up
     * with parent-plus-add-ons equal to what the customer is actually buying.
     */
    const total = items.reduce((sum, i) => sum + (i.final_price ?? i.price) * i.quantity, 0);

    return { ...cart, items, total_price: total, items_subtotal_price: total };
  }
}

const root = () => window.Shopify?.routes?.root ?? '/';

/* A family is always read in the same order, whichever member you arrive from,
   so the row does not reshuffle itself between two products. */
const ORDER = ['h-schuifpoorten', 'h-draaipoorten', 'vouwpoorten', 'looppoort', 'b-schuifpoorten', 'xxl-palen'];

/* Singular nouns, for a card that names what the sibling is rather than
   repeating the model name three times. */
const SINGULAR = {
  'h-schuifpoorten': 'Schuifpoort',
  'h-draaipoorten': 'Draaipoort',
  vouwpoorten: 'Vouwpoort',
  looppoort: 'Looppoort',
  'b-schuifpoorten': 'Zakelijke poort',
  'xxl-palen': 'XXL paal',
};

/* The trailing "-D" on "Draaipoort - AW 10.104-D" is a variant marker, not part
   of the family. Kept for display, dropped for matching — without that, five
   draaipoorten sit outside families they clearly belong to, one of them the
   fourth member of AW 10.104. */
const CODE = /\b(AW[.\s]?[A-Za-z]{0,2}[.\s]?\d+[.\d]*(?:\s*-\s*[A-Za-z]{1,2})?)\s*$/i;

/** The model code as it should be shown — "AW 10.76", "AW.AL.103", "AW 10.104-D". */
function modelCode(title) {
  const m = String(title ?? '').match(CODE);
  return m ? m[1].trim() : null;
}

/** The same code as a match key: case, dots, spaces and the variant suffix gone. */
function modelKey(title) {
  const code = modelCode(title);
  if (!code) return null;
  return (
    code
      .replace(/\s*-\s*[A-Za-z]{1,2}$/, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '') || null
  );
}

let catalogue = null;
function familyCatalogue() {
  if (catalogue) return catalogue;
  try {
    catalogue = JSON.parse(document.getElementById('awh-family')?.textContent ?? '[]');
  } catch {
    catalogue = [];
  }
  return catalogue;
}

const euroWhole = (n) => `€ ${Math.round(n).toLocaleString('nl-NL')}`;

/* The catalogue writes the same five colours sixteen different ways —
   "Zwart (verzinkt + RAL 9005)", "zwart-ral-9005", "RAL 9005 Zwart". Ported
   from lib/kleuren.js: map whatever it is given onto one name and one code, so
   the quote reads "Zwart RAL 9005" rather than the raw option value. A live
   data problem, not a display one, but the quote should not show the seams. */
const RAL = {
  9005: 'Zwart',
  7016: 'Antracietgrijs',
  7030: 'Steengrijs',
  6005: 'Mosgroen',
  9016: 'Verkeerswit',
  9010: 'Zuiver wit',
  8017: 'Chocoladebruin',
  7039: 'Kwartsgrijs',
};

function tidyColour(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (/aanvraag/i.test(raw)) return 'Andere RAL-kleur';
  const code = raw.toLowerCase().match(/ral[\s-]?(\d{4})/)?.[1];
  if (code && RAL[code]) return `${RAL[code]} RAL ${code}`;
  return raw.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/* "4000x1200" is a catalogue value, not a size anybody pictures. The
   configurator already speaks in metres; the quote should too. */
function tidySize(value) {
  const m = /^\s*(\d+)\s*[xX]\s*(\d+)\s*$/.exec(String(value ?? ''));
  if (!m) return String(value ?? '').trim();
  const metres = (mm) => (Number(mm) / 1000).toFixed(2).replace('.', ',');
  return `${metres(m[1])} × ${metres(m[2])} m`;
}

/** The free choices our configurator wrote onto a cart line, declines included. */
function freeOurs(item) {
  const raw = item?.properties?.['_awh_vrij'];
  if (!raw) return [];
  try {
    return JSON.parse(raw).map((o) => ({ name: o.n, key: o.k ?? o.n, label: o.v }));
  } catch {
    return [];
  }
}

/** Every option name Optionize owns on the line's product. */
function ozKeys(item) {
  const raw = item?.properties?.['_awh_keys'];
  if (!raw) return [];
  try {
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list.filter((k) => typeof k === 'string' && k) : [];
  } catch {
    return [];
  }
}

/** The priced options our configurator wrote onto a cart line. */
function ours(item) {
  const raw = item?.properties?.['_awh_opties'];
  if (!raw) return [];
  try {
    return JSON.parse(raw).map((o) => ({ name: o.n, key: o.k ?? o.n, label: o.v, price: Number(o.p) || 0 }));
  } catch {
    return [];
  }
}

const euro = (n) =>
  `€ ${n.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const esc = (s) =>
  String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
  );

const PLUS =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';

const CLOSE =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';

const CHEVRON =
  '<svg class="qsam__chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';

if (!customElements.get('awh-quote-component')) {
  customElements.define('awh-quote-component', AwhQuote);
}
